import React from 'react';
import {
  VStack,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  List,
  ListItem,
  ListIcon,
  Box,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import { SportsPageShell } from '../components/ui/SportsPageShell';
import { SportsCard } from '../components/ui/SportsCard';
import { useAppColors } from '../hooks/useAppColors';

const PRIZE_STAGES = [
  { etapa: 'Mejor Fase de Grupos (Jornadas 1, 2 y 3)', pct: '15%' },
  { etapa: 'Mejor Ronda de 16avos + 8avos', pct: '15%' },
  { etapa: 'Mejor Cuartos + Semis + 3er lugar + Final', pct: '20%' },
  { etapa: 'Ganador Absoluto del Torneo', pct: '50%', highlight: true },
];

const POINTS_RULES = [
  {
    points: 3,
    label: 'Marcador exacto',
    example: 'Predijiste 2-1 y fue 2-1',
    color: 'green',
  },
  {
    points: 1,
    label: 'Resultado correcto',
    example: 'Predijiste 2-1 y fue 3-1 (mismo ganador)',
    color: 'gold',
  },
  {
    points: 0,
    label: 'Sin acierto',
    example: 'No acertaste el resultado',
    color: 'gray',
  },
];

const TOURNAMENT_STAGES = [
  {
    title: 'Fase de Grupos',
    detail: 'El ganador es quien acumule más puntos en las 3 jornadas combinadas (Jornada 1, 2 y 3).',
  },
  {
    title: '16avos + 8avos de Final',
    detail: 'El ganador es quien acumule más puntos en estas dos rondas combinadas.',
  },
  {
    title: 'Cuartos + Semis + 3er lugar + Final',
    detail:
      'El ganador es quien acumule más puntos en estas rondas combinadas, incluyendo el partido por el tercer lugar.',
  },
  {
    title: 'Ganador Absoluto',
    detail: 'El participante con más puntos acumulados en todo el torneo.',
  },
];

interface ReglamentoSectionProps {
  number: number;
  title: string;
  children: React.ReactNode;
}

const ReglamentoSection: React.FC<ReglamentoSectionProps> = ({ number, title, children }) => {
  const { accent } = useAppColors();

  return (
    <SportsCard>
      <Heading size="md" mb={4} color={accent}>
        {number}. {title}
      </Heading>
      {children}
    </SportsCard>
  );
};

export const ReglamentoPage: React.FC = () => {
  const { text, muted, subtle, accent } = useAppColors();
  const exampleBg = useColorModeValue('pitch.50', 'pitch.900');
  const highlightRow = useColorModeValue('gold.50', 'rgba(229, 160, 32, 0.12)');
  const ruleBorder = useColorModeValue('gray.200', 'gray.600');

  return (
    <SportsPageShell
      title="Reglamento"
      subtitle="Quiniela Mundial FIFA 2026 — reglas de participación, puntos y premios"
      badge="Mundial 2026"
      maxW="container.lg"
    >
      <VStack align="stretch" spacing={5}>
        <ReglamentoSection number={1} title="Inscripción y Pago">
          <List spacing={3} color={text}>
            <ListItem display="flex" gap={2}>
              <ListIcon as={() => <Text color={subtle}>•</Text>} />
              Pago único de <strong>$200 MXN</strong> por participante.
            </ListItem>
            <ListItem display="flex" gap={2}>
              <ListIcon as={() => <Text color={subtle}>•</Text>} />
              Fecha límite de pago: <strong>15 de julio de 2026</strong> (antes de la final del
              torneo).
            </ListItem>
            <ListItem display="flex" gap={2}>
              <ListIcon as={() => <Text color={subtle}>•</Text>} />
              No se aceptan inscripciones después de la fecha límite ni pagos parciales.
            </ListItem>
            <ListItem display="flex" gap={2}>
              <ListIcon as={() => <Text color={subtle}>•</Text>} />
              El pozo total se define al cierre de inscripciones según el número de participantes
              confirmados.
            </ListItem>
          </List>
        </ReglamentoSection>

        <ReglamentoSection number={2} title="Distribución de Premios">
          <Text mb={4} color={muted}>
            El pozo se reparte en 4 etapas, cada una con su propio ganador:
          </Text>
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Etapa</Th>
                  <Th isNumeric>% del Pozo</Th>
                </Tr>
              </Thead>
              <Tbody>
                {PRIZE_STAGES.map((row) => (
                  <Tr key={row.etapa} bg={row.highlight ? highlightRow : undefined}>
                    <Td fontWeight={row.highlight ? 'semibold' : 'normal'}>{row.etapa}</Td>
                    <Td isNumeric fontWeight={row.highlight ? 'bold' : 'medium'}>
                      {row.pct}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </ReglamentoSection>

        <ReglamentoSection number={3} title="Captura de Pronósticos">
          <List spacing={3} color={text}>
            <ListItem display="flex" gap={2}>
              <ListIcon as={() => <Text color={subtle}>•</Text>} />
              Los pronósticos se capturan semanalmente al terminar cada jornada.
            </ListItem>
            <ListItem display="flex" gap={2}>
              <ListIcon as={() => <Text color={subtle}>•</Text>} />
              La captura se bloquea automáticamente al iniciar el primer partido de cada jornada.
            </ListItem>
            <ListItem display="flex" gap={2}>
              <ListIcon as={() => <Text color={subtle}>•</Text>} />
              Si no capturaste tus pronósticos antes del cierre, tu resultado en esa jornada es{' '}
              <strong>0 puntos</strong>, sin excepciones.
            </ListItem>
            <ListItem display="flex" gap={2}>
              <ListIcon as={() => <Text color={subtle}>•</Text>} />
              Debes pronosticar el marcador exacto de cada partido (ej. 2-1, 0-0).
            </ListItem>
          </List>
        </ReglamentoSection>

        <ReglamentoSection number={4} title="Sistema de Puntos">
          <Text mb={4} color={muted}>
            Por cada partido puedes obtener hasta 3 puntos:
          </Text>
          <VStack align="stretch" spacing={3}>
            {POINTS_RULES.map((rule) => (
              <Box
                key={rule.points}
                p={3}
                borderWidth="1px"
                borderRadius="md"
                borderColor={ruleBorder}
              >
                <Badge colorScheme={rule.color} mb={2}>
                  {rule.points} {rule.points === 1 ? 'punto' : 'puntos'}
                </Badge>
                <Text fontWeight="semibold" color={text}>
                  {rule.label}
                </Text>
                <Text fontSize="sm" color={muted} mt={1}>
                  {rule.example}
                </Text>
              </Box>
            ))}
          </VStack>
          <Text mt={4} fontSize="sm" color={muted}>
            Aplica tanto en fase de grupos como en eliminatorias. En eliminatorias se considera el
            resultado al final del tiempo reglamentario.
          </Text>
        </ReglamentoSection>

        <ReglamentoSection number={5} title="Criterio de Desempate">
          <Text mb={3} color={text}>
            Si dos o más participantes terminan una etapa con el mismo puntaje:
          </Text>
          <List spacing={2} color={text} mb={4}>
            <ListItem display="flex" gap={2}>
              <ListIcon as={() => <Text color={subtle}>1.</Text>} />
              Se suman todos los goles reales marcados en los partidos de la etapa.
            </ListItem>
            <ListItem display="flex" gap={2}>
              <ListIcon as={() => <Text color={subtle}>2.</Text>} />
              Se comparan contra la suma de goles pronosticados por cada participante empatado.
            </ListItem>
            <ListItem display="flex" gap={2}>
              <ListIcon as={() => <Text color={subtle}>3.</Text>} />
              Gana quien esté más cerca al total real.
            </ListItem>
            <ListItem display="flex" gap={2}>
              <ListIcon as={() => <Text color={subtle}>4.</Text>} />
              Si el empate persiste, el premio se reparte en partes iguales.
            </ListItem>
          </List>
          <Box p={4} borderRadius="md" bg={exampleBg}>
            <Text fontSize="sm" fontWeight="semibold" color={accent} mb={1}>
              Ejemplo
            </Text>
            <Text fontSize="sm" color={text}>
              La etapa tuvo 18 goles reales. El participante A pronosticó goles que suman 15
              (diferencia de 3) y el B sumó 20 (diferencia de 2). <strong>Gana B.</strong>
            </Text>
          </Box>
        </ReglamentoSection>

        <ReglamentoSection number={6} title="Etapas del Torneo">
          <VStack align="stretch" spacing={3}>
            {TOURNAMENT_STAGES.map((stage) => (
              <Box key={stage.title}>
                <Text fontWeight="semibold" color={accent}>
                  {stage.title}
                </Text>
                <Text fontSize="sm" color={muted}>
                  {stage.detail}
                </Text>
              </Box>
            ))}
          </VStack>
        </ReglamentoSection>

        <ReglamentoSection number={7} title="Disposiciones Generales">
          <List spacing={3} color={text}>
            <ListItem display="flex" gap={2}>
              <ListIcon as={() => <Text color={subtle}>•</Text>} />
              Los resultados oficiales son los del sitio oficial de la FIFA.
            </ListItem>
            <ListItem display="flex" gap={2}>
              <ListIcon as={() => <Text color={subtle}>•</Text>} />
              Cualquier situación no prevista será resuelta por consenso del grupo.
            </ListItem>
          </List>
          <Text mt={4} fontWeight="medium" color={accent}>
            ¡Mucho ánimo y que gane el mejor! 🏆
          </Text>
        </ReglamentoSection>
      </VStack>
    </SportsPageShell>
  );
};
